import React from 'react';
import {
  List,
  Datagrid,
  Filter,
  TextInput,
  Responsive,
  SimpleList,
  FunctionField,
  DateField,
  ShowButton,

} from 'react-admin';
import Avatar from '@material-ui/core/Avatar';
import { makeStyles } from '@material-ui/core/styles';
import Chip from '@material-ui/core/Chip';
import CommentIcon from '@material-ui/icons/Comment';

const placeholderAvatar = '/images/silhouette.png';

const useStyles = makeStyles(theme => ({
  comment: {
    marginBottom: theme.spacing(0.5),
  },
  messageCell: {
    maxWidth: 350,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  chip: {
    marginRight: theme.spacing(1),
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  showButton: {
    marginLeft: theme.spacing(1),
  },
  avatar: {
    width: 40,
    height: 40,
    marginRight: theme.spacing(1),
  },
  datagrid: {
    '& .RaDatagrid-headerCell': {
      backgroundColor: '#f5f5f5',
      fontWeight: 'bold',
      padding: '16px 8px',
      borderBottom: '2px solid #e0e0e0',
    },
    '& .RaDatagrid-tbody': {
      '& tr': {
        '&:hover': {
          backgroundColor: '#f9f9f9',
        },
      },
    },
  },
  commentText: {
    padding: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    borderRadius: theme.spacing(1),
    border: '1px solid #e0e0e0',
    fontSize: '0.9rem',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    marginBottom: theme.spacing(1),
  },
  commentDate: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  filterForm: {
    display: 'flex',
    alignItems: 'flex-end',
    width: '100%',
    marginBottom: theme.spacing(2),
    '& .MuiInputBase-root': {
      marginRight: theme.spacing(1),
    },
  },
  searchInput: {
    marginBottom: 0,
  },
  filterButton: {
    marginLeft: theme.spacing(1),
  },
  filterIcon: {
    marginRight: theme.spacing(0.5),
  },
}));

const AuthorAvatarField = ({ record = {} }) => {
  const classes = useStyles();
  const author = record.author || {};
  const src = author.picture || placeholderAvatar;
  const alt = `${author.firstName || ''} ${author.lastName || ''}`.trim() || 'User avatar';
  return (
    <Avatar
      alt={alt}
      src={src}
      imgProps={{
        onError: e => {
          e.target.onerror = null;
          e.target.src = placeholderAvatar;
        },
      }}
      className={classes.avatar}
    />
  );
};

const CommentContentField = ({ record }) => {
  const classes = useStyles();
  return (
    <div>
      <div className={classes.commentText}>{record.message}</div>
      <div className={classes.commentDate}>
      </div>
    </div>
  );
};

const ContentChipField = ({ record }) => {
  const classes = useStyles();
  return record && record.contentTitle ? (
    <Chip 
      icon={<CommentIcon />} 
      label={record.contentTitle} 
      size="small" 
      className={classes.chip}
    />
  ) : null;
};

const CommentFilter = props => {
  const classes = useStyles();
  return (
    <Filter {...props}>
      <TextInput
        label="Search message"
        source="message"
        alwaysOn
        className={classes.searchInput}
        fullWidth
        resettable
      />
      <TextInput 
        label="Commented Content" 
        source="contentTitle" 
        alwaysOn 
        className={classes.searchInput}
        fullWidth
        resettable
      />
    </Filter>
  );
};

export const CommentList = props => {
  const classes = useStyles();
  return (
    <List 
      {...props} 
      filters={<CommentFilter />} 
      perPage={25} 
      sort={{ field: 'createdAt', order: 'DESC' }}
      bulkActionButtons={false}
      title="User Comments"
    >
      <Responsive
        small={
          <SimpleList
            primaryText={record => record.author ? `${record.author.firstName} ${record.author.lastName}` : ''}
            secondaryText={record => record.message}
            tertiaryText={record => (record.author && record.author.email) || ''}
            leftAvatar={record => <AuthorAvatarField record={record} />}
          />
        }
        medium={
          <Datagrid optimized className={classes.datagrid}>
            <FunctionField label="Avatar" render={record => <AuthorAvatarField record={record} />} />
            <FunctionField label="Author" render={record => record.author ? `${record.author.firstName} ${record.author.lastName}`: ''} />
              
              <FunctionField 
              label="Commented On" 
              source="contentTitle"
              render={record => <ContentChipField record={record} />}
            />
            <FunctionField 
              label="Comment" 
              render={record => <CommentContentField record={record} />} 
            />
            <DateField source="createdAt" label="Date" showTime sortByOrder="DESC" />
            <ShowButton label="View User" basePath="/users" record={record => ({ id: record.authorId })} />
          </Datagrid>
        }
      />
    </List>
  );
};
